import { MoreVert } from "@mui/icons-material";
import { Divider, IconButton } from "@mui/material";
import theme from "Theme/AppTheme";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { downloadCSVFromBlob } from "components/CSVExport/utils";
import { ExportToCSVAsync } from "components/CSVExport/ExportToCSVAsync";
import { AcxViewSqlDialog } from "components/UI/AcxViewSqlDialog";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import React, { useEffect, useMemo, useState } from "react";
import { toPng } from "html-to-image";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { styleClsx } from "utils/styleClsx";
import ChartCardStore from "../ChartCardStore";
import { ChartConfirmDelete } from "./ChartConfirmDelete";
import { ChartPinToDashboardDialog } from "./ChartPinToDashboardDialog";
import { useCurrentDashboard } from "../../../useCurrentDashboard";
import {
    ISignalsChartDefinition,
    SignalsObjectType,
} from "../../../SignalsModels";
import { AuthStore } from "stores/AuthStore";
import SignalsPageStore from "../../../Store/SignalsPageStore";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import ObjectConfigurationDrawer from "../../ObjectConfiguration/ObjectConfigurationDrawer";
import { useObjectMenu } from "../../useObjectMenu";

type Props = {
    store: ChartCardStore;
    chartContainerRef: React.RefObject<HTMLDivElement>;
    isValidData: boolean;
};

export const ChartMenu = observer(
    ({ store, chartContainerRef, isValidData }: Props) => {
        const currentDashboard = useCurrentDashboard();
        const messageStore = useStore(MessageStore);
        const authStore = useStore(AuthStore);
        const signalsPageStore = useStore(SignalsPageStore);
        const drawerStore = useStore(LayoutDrawerStore);

        const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
        const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
        const [pinDialogOpen, setPinDialogOpen] = useState(false);
        const [isExportingCsv, setIsExportingCsv] = useState(false);
        const [isSelectExportColumnsOpen, setIsSelectExportColumnsOpen] =
            useState(false);
        const isRefreshing = store.isRefreshing;

        const {
            menuAnchor,
            isMenuOpen,
            setIsMenuOpen,
            handleMenuButtonClick,
            handleMenuButtonKeyDown,
        } = useObjectMenu();

        const exportStrategy = store.config.capabilities.csvExport;
        const isExportableAsCsv = exportStrategy !== "none";
        const allowColumnSelection = store.allowColumnSelection;

        const sqlQuery = store.displayedSqlQuery;
        const showSql = !!sqlQuery && authStore.isUserUltra();

        const canConfigure = store.isEditable;

        const chartDefinition = useMemo(() => {
            return currentDashboard?.signalsObjects.find(
                (obj) =>
                    obj.id === store.id &&
                    obj.objectType === SignalsObjectType.Chart,
            );
        }, [currentDashboard?.signalsObjects, store.id]);

        const canDelete = store.isDeletable;

        const handleExportPng = async () => {
            if (!chartContainerRef.current) return;

            const elementsToHide =
                chartContainerRef.current.querySelectorAll(".no-export");

            const originalDisplays: string[] = [];
            elementsToHide.forEach((el, i) => {
                originalDisplays[i] = (el as HTMLElement).style.display;
                (el as HTMLElement).style.display = "none";
            });

            // Selections replace the hidden dropdowns in the image.
            const elementsToShow =
                chartContainerRef.current.querySelectorAll<HTMLElement>(
                    ".export-only",
                );
            elementsToShow.forEach((el) => {
                el.style.display = "block";
            });

            try {
                // Fonts are embedded: the fallback font is wider, so text drawn with it no
                // longer fits the boxes laid out on screen.
                const dataUrl = await toPng(chartContainerRef.current);
                const link = document.createElement("a");
                link.download = `${store.title ?? "Signals-Chart-Export"}.png`;
                link.href = dataUrl;
                link.click();
            } finally {
                elementsToHide.forEach((el, i) => {
                    (el as HTMLElement).style.display = originalDisplays[i];
                });
                elementsToShow.forEach((el) => {
                    el.style.display = "";
                });
            }
        };

        const handleExportCsv = async (selectedColumns?: string[]) => {
            setIsExportingCsv(true);
            try {
                const blob = await store.exportCsv(selectedColumns);
                if (!blob) {
                    messageStore.logError(
                        "Failed to export chart data as CSV.",
                    );
                    return;
                }
                downloadCSVFromBlob(
                    blob,
                    `${store.title ?? "Signals-Chart-Export"}.csv`,
                );
            } finally {
                setIsExportingCsv(false);
            }
        };

        const startExportCsv = async () => {
            if (!isExportableAsCsv) return;

            if (allowColumnSelection) {
                await store.loadExportableFields();
                if (store.exportableFieldNodes.length === 0) {
                    messageStore.logError(
                        "No exportable fields are available for this chart.",
                    );
                    return;
                }
                setIsSelectExportColumnsOpen(true);
            } else {
                await handleExportCsv();
            }
            setIsMenuOpen(false);
        };

        const hasPinTargets = signalsPageStore.pinTargetDashboards.some(
            (dash) => dash.id !== currentDashboard?.id,
        );

        const pinDisabled = !chartDefinition || !hasPinTargets;

        const shouldHideMenu = currentDashboard?.isPresentationMode;

        useEffect(() => {
            if (!isRefreshing) return;
            setIsMenuOpen(false);
        }, [isRefreshing, setIsMenuOpen]);

        const openConfigDrawer = () => {
            drawerStore.closeAndResetDrawer();
            drawerStore.restoreDefaults();
            drawerStore.setOffsetPixels(defaultDrawerOffset);
            drawerStore.setCustomRootStyles({
                zIndex: theme.zIndex.appBar - 1,
            });
            drawerStore.setContentFactory(() => (
                <ObjectConfigurationDrawer store={store} />
            ));
            drawerStore.setOpen(true);
        };

        if (shouldHideMenu || !store.config.renderOptions.menu) {
            return null;
        }

        return (
            <>
                <IconButton
                    id={`${store.id}-menu`}
                    ref={menuAnchor}
                    style={styleClsx({
                        color: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.black.main,
                        border: isRefreshing ? "none" : "1px solid",
                        borderColor: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.gray.light,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.white.main,
                        cursor: isRefreshing ? "default" : "pointer",
                    })}
                    onClick={handleMenuButtonClick}
                    onKeyDown={handleMenuButtonKeyDown}
                    size="small"
                    disabled={isRefreshing}
                >
                    <MoreVert />
                </IconButton>
                <AcxMenuV2
                    anchorEl={menuAnchor.current}
                    open={isMenuOpen}
                    onClose={() => setIsMenuOpen(false)}
                    disableAutoFocusItem
                >
                    <AcxMenuItem
                        id={`${store.id}-refresh`}
                        onClick={() => {
                            store.runRefresh(true);
                            setIsMenuOpen(false);
                        }}
                        disabled={store.getTaskLoading("refresh")}
                    >
                        Refresh
                    </AcxMenuItem>
                    <AcxMenuItem
                        id={`${store.id}-pin-to-dashboard`}
                        onClick={() => {
                            setPinDialogOpen(true);
                            setIsMenuOpen(false);
                        }}
                        disabled={pinDisabled}
                    >
                        Pin to dashboard
                    </AcxMenuItem>
                    {canConfigure && (
                        <AcxMenuItem
                            id={`${store.id}-edit`}
                            onClick={() => {
                                openConfigDrawer();
                                setIsMenuOpen(false);
                            }}
                            disabled={isRefreshing}
                        >
                            Configure
                        </AcxMenuItem>
                    )}
                    {(isValidData || isExportableAsCsv || showSql) && (
                        <Divider style={{ margin: "0px" }} />
                    )}
                    {isValidData && (
                        <AcxMenuItem
                            id={`${store.id}-export-png`}
                            onClick={() => {
                                handleExportPng();
                                setIsMenuOpen(false);
                            }}
                            disabled={!isValidData}
                        >
                            Export as PNG
                        </AcxMenuItem>
                    )}
                    {isExportableAsCsv && (
                        <AcxMenuItem
                            id={`${store.id}-export-csv`}
                            onClick={startExportCsv}
                            disabled={isExportingCsv || !isValidData}
                        >
                            Export CSV
                        </AcxMenuItem>
                    )}
                    {showSql && (
                        <AcxMenuItem
                            id={`${store.id}-sql`}
                            onClick={() => {
                                setSqlDialogOpen(true);
                                setIsMenuOpen(false);
                            }}
                        >
                            View SQL
                        </AcxMenuItem>
                    )}
                    {canDelete && <Divider style={{ margin: "0px" }} />}
                    {canDelete && (
                        <AcxMenuItem
                            id={`${store.id}-delete`}
                            onClick={() => {
                                setDeleteDialogOpen(true);
                                setIsMenuOpen(false);
                            }}
                            sx={{
                                color: theme.palette.error.main,
                            }}
                        >
                            Delete
                        </AcxMenuItem>
                    )}
                </AcxMenuV2>
                <ChartConfirmDelete
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    store={store}
                />
                <ChartPinToDashboardDialog
                    open={pinDialogOpen}
                    onClose={() => setPinDialogOpen(false)}
                    chartDefinition={
                        chartDefinition as ISignalsChartDefinition | undefined
                    }
                    pinState={store}
                />
                {showSql && (
                    <AcxViewSqlDialog
                        sqlStatements={[sqlQuery ?? ""]}
                        title={`SQL Query for ${store.title}`}
                        isModalOpen={sqlDialogOpen}
                        onClose={() => setSqlDialogOpen(false)}
                    />
                )}
                {allowColumnSelection && (
                    <ExportToCSVAsync
                        open={isSelectExportColumnsOpen}
                        onOpenChange={setIsSelectExportColumnsOpen}
                        fields={store.exportableFieldNodes}
                        onExport={async (columns) => {
                            await handleExportCsv(columns);
                            setIsSelectExportColumnsOpen(false);
                        }}
                        title="Select columns to export"
                    />
                )}
            </>
        );
    },
);
